import CategoryMeasurementSection from '@c/CategoryMeasurementSection';
import FormCurrencyInputFancy from '@c/forms/controls/FormCurrencyInputFancy';
import FormLabel from '@c/forms/controls/FormLabel';
import { CheckmarkIcon } from '@c/icons';
import ShippingLabel from '@c/listing/components/ShippingLabel';
import AddAddressModal from '@c/modals/AddAddressModal';
import GenericTruck from '@i/listing/generic-truck.svg';
import MotorsportLockerShipping from '@i/listing/shipping-truck.svg';
import { Divider } from '@ui/Divider';
import RadioButton from '@ui/RadioButton';
import SafeImage from '@ui/SafeImage';
import Select from '@ui/Select';
import Toggle from '@ui/Toggle';
import {
  getNumber,
  getShippingRegionOption,
  ShippingRegionOptionsType,
} from '@util/createListingHelpers';
import { useUserAddresses } from '@util/hooks/useUserAddresses';
import { isMobile } from '@util/index';
import { cx } from 'class-variance-authority';
import { useAuth } from 'context/AuthContext';
import { PlusIcon } from 'i/plus';
import { Dimensions, ProductDocument, Shipment, Weight } from 'models/product';
import { useEffect, useState } from 'react';
import { useCreateListing } from '../_context';
import { CreateListingInfoSubsection } from './CreateListingInfoSubSection';
import { CreateListingSection } from './CreateListingSection';

const DEFAULT_SHIPPING: ProductDocument['shipping_costs'] = [
  {
    code: 'US',
    cost: 0,
    name: 'United States',
    enabled: true,
  },
];

const DEFAULT_MX_SHIPPING_LABEL: Omit<Shipment, 'ship_from'> = {
  package: {
    weight: {
      value: 0,
      unit: 'ounce',
    },
    dimensions: {
      unit: 'inch',
      length: 0,
      width: 0,
      height: 0,
    },
    insurance_provider: 'none',
    insured_value: {
      currency: 'usd',
      amount: 0,
    },
  },
};

export const Shipping = () => {
  const { form, product, categoryInfo } = useCreateListing();
  const { userDoc } = useAuth();
  const allAddresses = useUserAddresses();
  const [insuranceEnabled, setInsuranceEnabled] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);

  const errorMessage = Array.isArray(form.formState.errors.shipping_costs)
    ? form.formState.errors.shipping_costs?.filter(
        (o: any) => o?.cost.message
      )[0]?.cost?.message
      ? 'All enabled shipping regions must have a cost'
      : ''
    : form.formState.errors.shipment?.package?.insured_value?.message ?? '';

  function toggleFlatRateShipping(enabled: boolean) {
    if (enabled) {
      form.setValue(
        'shipping_costs',
        product?.shipping_costs ?? product.shipping_costs ?? DEFAULT_SHIPPING
      );
      form.setValue('shipment', undefined);
    } else {
      form.setValue(
        'shipment',
        product.shipment ??
          product?.shipment ?? {
            ...DEFAULT_MX_SHIPPING_LABEL,
            ...(userDoc?.addresses?.[0]! && {
              ship_from: userDoc.addresses[0],
            }),
          }
      );
    }
    form.setValue('is_flat_rate', enabled);
  }

  useEffect(() => {
    if (!product.is_flat_rate && !product.shipment?.package.dimensions?.unit) {
      form.setValue('shipment.package.dimensions.unit', 'inch');
    }
  }, [product.is_flat_rate, product.shipment?.package?.dimensions?.unit, form]);

  const handleShippingFromChange = (option: { value: string }) => {
    const address = allAddresses.find(
      (a) =>
        `${a.address_line1}, ${a.city_locality}, ${a.state_province} ${a.postal_code}` ===
        option.value
    );
    if (address) {
      form.setValue('shipment.ship_from', address);
    }
  };

  const isLocalPickupEnable = categoryInfo.some(
    (cat) => cat.is_local_pickup_allowed
  );

  const catsWithWeight = categoryInfo.filter((c) => !!c.package?.weight);
  const selectedCat = catsWithWeight.at(-1);

  return (
    <CreateListingSection
      title="Shipping"
      error={
        form.formState.errors.shipping_costs?.message ||
        form.formState.errors.shipment?.package?.message ||
        errorMessage
      }
    >
      <div className="flex w-full flex-col gap-x-16 sm:flex-row">
        <div className="w-full">
          <AddAddressModal
            isOpen={addressModalOpen}
            dismiss={(address) => {
              setAddressModalOpen(false);
              if (address && !product.is_flat_rate) {
                form.setValue('shipment.ship_from', address);
              }
            }}
          />
          <div className="mt-8 flex w-full gap-[3.2rem]">
            <div className="flex flex-col gap-12">
              {!product.is_auction && (
                <>
                  <div className="hidden md:flex">
                    <FormLabel value="Choose Shipping Fulfillment" required>
                      <div className="mt-6 flex gap-12">
                        {/*<RadioButton
                          label="Use Motorsport Locker Shipping Label"
                          checked={
                            !product?.is_flat_rate && !product.is_auction
                          }
                          onClick={() => toggleFlatRateShipping(false)}
                        />*/}

                        <RadioButton
                          label="Use My Own Shipping Label"
                          checked={product?.is_flat_rate || product.is_auction}
                          onClick={() => toggleFlatRateShipping(true)}
                        />
                        {/*
                        {isLocalPickupEnable ? (
                          <RadioButton
                            label="Use Local pickup"
                            checked={
                              !product?.is_flat_rate && !product.is_auction
                            }
                            onClick={() => toggleFlatRateShipping(false)}
                          />
                        ) : null}*/}
                      </div>
                    </FormLabel>
                  </div>
                  {/* <div className="flex w-full md:hidden">
                    <AnimatedToggle
                      onChange={(val) => toggleFlatRateShipping(val)}
                      value={product?.is_flat_rate}
                      options={[
                        // { label: 'Motorsport Locker Shipping', value: false },
                        { label: 'Ship on your own', value: true },
                      ]}
                    />
                  </div> */}
                </>
              )}
              <ShippingLabelBanner isFlatRate={product?.is_flat_rate} />
              {!product?.is_flat_rate && !product.is_auction && (
                <>
                  <div className="flex w-full flex-col gap-4">
                    <FormLabel
                      value="Shipping From"
                      className="!text-[1.8rem]"
                      required={true}
                    >
                      <div className="flex items-center gap-x-4 ">
                        <div className="w-full sm:max-w-[unset]">
                          {!userDoc?.roles?.admin && (
                            <Select
                              allowEmpty={false}
                              error={
                                !!form.formState.errors?.shipment?.ship_from
                              }
                              options={allAddresses.map((address) => ({
                                id: `${address.address_line1}, ${address.city_locality}, ${address.state_province} ${address.postal_code}`,
                                value: `${address.address_line1}, ${address.city_locality}, ${address.state_province} ${address.postal_code}`,
                                label: `${address.address_line1}, ${address.city_locality}, ${address.state_province} ${address.postal_code}`,
                              }))}
                              value={
                                product.shipment?.ship_from
                                  ? `${product.shipment.ship_from.address_line1}, ${product.shipment.ship_from.city_locality}, ${product.shipment.ship_from.state_province} ${product.shipment.ship_from.postal_code}`
                                  : ''
                              }
                              onChange={handleShippingFromChange}
                              className="w-full border border-[#d9d9d9]"
                            />
                          )}
                        </div>

                        {!!form.formState.errors?.shipment?.ship_from && (
                          <span className="text-[1.5rem] text-brand-secondary">
                            {
                              form.formState.errors?.shipment?.ship_from
                                ?.message
                            }
                          </span>
                        )}
                      </div>
                    </FormLabel>
                    <button
                      className="flex w-fit items-center gap-2"
                      onClick={() => setAddressModalOpen(true)}
                      type="button"
                    >
                      <PlusIcon />
                      <span className="text-body-md font-semibold text-text-red">
                        Add new Address
                      </span>
                    </button>
                  </div>

                  <ShippingLabel
                    form={product}
                    register={form.register}
                    setError={form.setError}
                    clearErrors={form.clearErrors}
                    handleWeightChange={(weight: number) => {
                      if (
                        selectedCat?.pkg_ranges?.weight?.min &&
                        weight < selectedCat.pkg_ranges?.weight?.min * 16
                      ) {
                        form.setError('shipment.package.weight', {
                          message: `Weight must be at least ${selectedCat.pkg_ranges?.weight?.min} lbs`,
                        });
                      } else if (
                        selectedCat?.pkg_ranges?.weight?.max &&
                        weight > selectedCat.pkg_ranges?.weight?.max * 16
                      ) {
                        form.setError('shipment.package.weight', {
                          message: `Weight must be at most ${selectedCat.pkg_ranges?.weight?.max} lbs`,
                        });
                      } else {
                        form.clearErrors('shipment.package.weight');
                      }
                      form.setValue('shipment.package.weight', {
                        value: weight,
                        unit: 'ounce',
                      });
                    }}
                    measurementSection={
                      product.category !== 'Collectibles' && selectedCat ? (
                        <CategoryMeasurementSection
                          category={selectedCat.name}
                          dimensions={{
                            length: selectedCat.package?.length ?? 0,
                            width: selectedCat.package?.width ?? 0,
                            height: selectedCat.package?.height ?? 0,
                            unit: 'inch',
                          }}
                          weight={{
                            value: selectedCat.package?.weight
                              ? selectedCat.package.weight * 16
                              : 0,
                            unit: 'ounce',
                          }}
                          onSelect={(m: {
                            weight: Weight;
                            dimensions: Dimensions;
                          }) => {
                            const weight = m.weight;
                            const dimensions = m.dimensions;

                            form.setValue('shipment.package.dimensions', {
                              length: dimensions.length,
                              width: dimensions.width,
                              height: dimensions.height,
                              unit: 'inch',
                            });
                            form.setValue('shipment.package.weight', {
                              value: weight.value,
                              unit: 'ounce',
                            });
                          }}
                        />
                      ) : (
                        <CategoryMeasurementSection
                          category={'Collectibles'}
                          weight={{
                            value: 0,
                            unit: 'ounce',
                          }}
                          dimensions={{
                            length: 0,
                            width: 0,
                            height: 0,
                            unit: 'inch',
                          }}
                          onSelect={(m: {
                            weight: Weight;
                            dimensions: Dimensions;
                          }) => {
                            const weight = m.weight;
                            const dimensions = m.dimensions;
                            form.setValue('shipment.package.dimensions', {
                              length: dimensions.length,
                              width: dimensions.width,
                              height: dimensions.height,
                              unit: 'inch',
                            });
                            form.setValue('shipment.package.weight', {
                              value: weight.value,
                              unit: 'ounce',
                            });
                          }}
                        />
                      )
                    }
                    insuranceEnabled={insuranceEnabled}
                    toggleShipInsurance={() => {
                      form.clearErrors('shipment.package.insured_value');

                      if (product.shipment?.package.insured_value?.amount) {
                        form.setValue('shipment.package.insured_value', {
                          currency: 'usd',
                          amount: 0,
                        });

                        form.setValue(
                          'shipment.package.insurance_provider',
                          'none'
                        );
                      } else {
                        form.setValue('shipment.package.insured_value', {
                          currency: 'usd',
                          amount: product.price ? Number(product.price) : 0,
                        });

                        form.setValue(
                          'shipment.package.insurance_provider',
                          'mx_locker'
                        );
                      }

                      setInsuranceEnabled(!insuranceEnabled);
                    }}
                    errors={form.formState.errors}
                    hideInsuranceOption={false}
                    selectedCat={selectedCat}
                  />
                </>
              )}
              {product?.is_flat_rate || product.is_auction ? (
                <ShippingRegionsOptions />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </CreateListingSection>
  );
};

const ShippingRegionsOptions = () => {
  const { form, product } = useCreateListing();

  const [freeShipEnable, setFreeShipEnable] = useState(false);

  function updateRegionalShippingCost(
    opt: ShippingRegionOptionsType,
    e: React.ChangeEvent<HTMLInputElement>
  ) {
    const cost = e.target.value !== '' ? getNumber(e.target.value) : '';

    const shippingRegions = product.shipping_costs ?? [];
    const newShippingRegions = shippingRegions.map((r) => {
      if (r.code === opt.id) {
        return {
          ...r,
          cost,
        };
      }
      return r;
    });
    form.setValue('shipping_costs', newShippingRegions as any);
  }

  const toggleLocation = (opt: ShippingRegionOptionsType) => {
    const isEnabled =
      product?.shipping_costs &&
      product?.shipping_costs?.findIndex((o) => o.code === opt.id) > -1;

    const shippingRegions = product.shipping_costs ?? [];

    const newShippingRegions = isEnabled
      ? shippingRegions.filter((r) => {
          return r.code !== opt.id;
        })
      : shippingRegions.concat({
          code: opt.id,
          name: opt.label,
          cost: 0,
          enabled: true,
        });

    form.setValue('shipping_costs', newShippingRegions);
  };

  const getDefaultValue = (opt: ShippingRegionOptionsType) => {
    return (
      (product?.shipping_costs &&
        product?.shipping_costs
          ?.find((o) => o.code === opt.id)
          ?.cost?.toString()) ??
      ''
    );
  };

  const isToggleEnabled = (opt: ShippingRegionOptionsType) => {
    return Boolean(
      product?.shipping_costs &&
        product?.shipping_costs?.findIndex((o) => o.code === opt.id) > -1
    );
  };

  const getCurrentValue = (opt: ShippingRegionOptionsType) => {
    return (
      (product?.shipping_costs &&
        product?.shipping_costs
          ?.find((o) => o.code === opt.id)
          ?.cost?.toString()) ??
      ''
    );
  };

  const onFreeShippingToggle = () => {
    if (freeShipEnable) {
      setFreeShipEnable(false);
      form.setValue('shipping_costs', DEFAULT_SHIPPING);
      return;
    }
    setFreeShipEnable(true);
  };

  return (
    <FormLabel value="Shipping To" required={true}>
      <div className="flex flex-col gap-8">
        <p className="text-body-sm font-medium text-text-muted">
          You control where you ship and what you charge.
        </p>
        <div className="flex flex-col gap-8">
          <div className="grid w-full grid-cols-1 justify-between md:grid-cols-2 md:gap-12">
            {getShippingRegionOption().map((opt) => (
              <>
                <div className="hidden flex-col gap-2 md:flex">
                  <label className="text-body-md font-semibold">
                    {opt.label}
                  </label>
                  <div className="flex items-center gap-8">
                    <div className="max-w-96">
                      <FormCurrencyInputFancy
                        placeholder={'Enter a cost'}
                        allowEmpty
                        value={getCurrentValue(opt)}
                        onChange={(e) => updateRegionalShippingCost(opt, e)}
                        defaultValue={getDefaultValue(opt)}
                      />
                    </div>
                    <Toggle
                      enabled={isToggleEnabled(opt)}
                      onChange={() => toggleLocation(opt)}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-4 md:hidden">
                  <div className="flex w-full items-center justify-between ">
                    <span className="text-body-md font-semibold">
                      {opt.label}
                    </span>
                    <Toggle
                      enabled={isToggleEnabled(opt)}
                      onChange={() => toggleLocation(opt)}
                    />
                  </div>
                  {isToggleEnabled(opt) && isMobile() ? (
                    <div className="flex w-full flex-col gap-4">
                      <span className="text-body-sm font-medium text-text-muted">
                        Set a shipping cost and purchase your own label at
                        preferred carrier.
                      </span>
                      <FormCurrencyInputFancy
                        placeholder={'Enter a cost'}
                        allowEmpty
                        value={getCurrentValue(opt)}
                        onChange={(e) => updateRegionalShippingCost(opt, e)}
                        defaultValue={getDefaultValue(opt)}
                      />
                      {opt.id === 'US' ? (
                        <div className="mt-2 flex gap-4">
                          <button
                            className={`mt-1 flex size-9 items-center justify-center rounded-lg transition-all duration-200 ease-in-out ${
                              freeShipEnable
                                ? 'scale-[1.05] bg-brand-secondary'
                                : 'scale-100 border border-brand-dark-gray'
                            }`}
                            type="button"
                            onClick={onFreeShippingToggle}
                          >
                            <div
                              className={cx(
                                '-mr-[0.4px] mt-[0.3px] flex w-full items-center justify-center transition-opacity duration-200',
                                freeShipEnable ? 'opacity-100' : 'opacity-0'
                              )}
                            >
                              <CheckmarkIcon
                                height={6}
                                width={10}
                                variant="white"
                              />
                            </div>
                          </button>
                          <div className="flex flex-col gap-2">
                            <span className="text-body-sm font-semibold">
                              Offer Free Shipping
                            </span>
                            <span className="text-body-sm font-medium text-text-muted">
                              Only applies if buyer chooses standard shipping.
                            </span>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <div className="my-8 block md:hidden">
                  <Divider />
                </div>
              </>
            ))}
          </div>
          <p className="text-body-sm font-medium text-text-muted">
            By selecting manual shipping, you are assuming responsibility for
            any issues that arise in shipping and handling. Please consider
            purchasing shipping insurance for big-ticket items!
          </p>
        </div>
      </div>
    </FormLabel>
  );
};

const ShippingLabelBanner = ({ isFlatRate }: { isFlatRate: boolean }) => {
  return !isFlatRate ? (
    <div className="w-full  bg-[#f6f6f6] p-8">
      <div className="text-bold mb-4 w-fit rounded-xl bg-brand-secondary px-4 py-1 text-label uppercase text-white">
        Recommended
      </div>
      <div className="mb-4 flex items-center gap-4 md:mb-0 md:gap-8">
        <SafeImage
          src={MotorsportLockerShipping}
          alt="motorsport truck icon"
          classes="size-16 md:size-32"
        />
        <div className="flex flex-col gap-2">
          <h5 className="text-body-lg font-bold">
            Use Motorsport Locker Shipping Label
          </h5>
          <p className="hidden text-body-md font-medium text-text-muted md:block">
            USPS First Class Mail, Priority Mail, or UPS Ground - customer
            selects at checkout.
          </p>
        </div>
      </div>
      <p className="block text-body-md font-medium text-text-muted md:hidden">
        Customer pays at checkout. You are provided with a pre-paid label.
      </p>
    </div>
  ) : (
    <div className="w-full bg-[#f6f6f6] p-8">
      <div className="mb-4 flex items-center gap-4 md:mb-0 md:gap-8">
        <SafeImage
          src={GenericTruck}
          alt="generic truck icon"
          classes="size-16 md:size-32"
        />
        <div className="flex flex-col gap-2">
          <h5 className="text-body-lg font-bold">Use My Own Shipping Label</h5>
          <p className="hidden text-body-md font-medium text-text-muted md:block">
            After your listing is sold, we&apos;ll ask you to provide your own
            shipping label and upload the tracking number to the Motorsport
            Locker website.
          </p>
        </div>
      </div>
      <p className="block text-body-md font-medium text-text-muted md:hidden">
        After your listing is sold, you will be responsible for providing your
        own tracking information.
      </p>
    </div>
  );
};
